import { Box, Button, Chip, Tooltip, Typography, useTheme, alpha } from '@mui/material';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import dayjs from 'dayjs';
import { BillingPeriodToggle } from '../BillingPeriodToggle';
import type { BillingPeriod } from '../../utils/paymentTiers';
import type { Catalog } from '../../utils/subscriptionApi';

interface CatalogRowProps {
  catalog: Catalog;
  onSwitchCycle: (catalogAliId: string, target: BillingPeriod) => void;
  onRemove?: (catalogAliId: string) => void;
  /** GH#1254: start a reactivation checkout for an EXPIRED catalog. */
  onReactivate?: (catalogAliId: string) => void;
}

function renewLabel(catalog: Catalog): string {
  if (catalog.status === 'expired') return 'Expired';
  if (!catalog.renewDate) return '—';
  const formatted = dayjs(catalog.renewDate).isValid()
    ? dayjs(catalog.renewDate).format('MMM D, YYYY')
    : null;
  if (!formatted) return '—';
  return catalog.cancelAtPeriodEnd ? `Cancels ${formatted}` : `Renews ${formatted}`;
}

function getTierChipSx(isPrimary: boolean, theme: ReturnType<typeof useTheme>) {
  return isPrimary
    ? {
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        color: theme.palette.primary.main,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
        fontSize: 10,
      }
    : {
        bgcolor: 'background.paper',
        color: 'text.secondary',
        border: '1px solid',
        borderColor: 'divider',
        fontSize: 10,
      };
}

export const CatalogRow: React.FC<CatalogRowProps> = ({
  catalog,
  onSwitchCycle,
  onRemove,
  onReactivate,
}) => {
  const theme = useTheme();
  const tierLabel = catalog.isPrimary ? 'Primary' : 'Add-on';
  const priceLabel = catalog.billingPeriod === 'yearly' ? '$120/yr' : '$15/mo';
  const isExpired = catalog.status === 'expired';
  const toggleDisabled = catalog.cancelAtPeriodEnd || isExpired;
  const tooltipTitle = toggleDisabled ? 'Reactivate to change billing period' : '';

  return (
    <Box
      data-testid={`catalog-row-${catalog.catalogAliId}`}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ mt: '2px' }}>
        <PaletteOutlinedIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 600 }}>{catalog.name}</Typography>
          <Chip
            label={tierLabel}
            size="small"
            sx={getTierChipSx(catalog.isPrimary, theme)}
          />
          {catalog.cancelAtPeriodEnd && !isExpired && (
            <Chip
              label="Cancelling"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: 10 }}
            />
          )}
          {isExpired && (
            <Chip
              label="Expired"
              size="small"
              color="error"
              variant="outlined"
              sx={{ fontSize: 10 }}
              data-testid={`catalog-expired-chip-${catalog.catalogAliId}`}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          <Box component="span" sx={{ letterSpacing: '0.02em', fontWeight: 500 }}>
            {catalog.catalogAliId}
          </Box>
          <Box component="span" sx={{ mx: 0.75, color: 'divider' }}>·</Box>
          Creator: {catalog.creatorName}
          <Box component="span" sx={{ mx: 0.75, color: 'divider' }}>·</Box>
          {renewLabel(catalog)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <Tooltip title={tooltipTitle} disableHoverListener={!toggleDisabled}>
          <span>
            <BillingPeriodToggle
              value={catalog.billingPeriod}
              onChange={(period) => onSwitchCycle(catalog.catalogAliId, period)}
              ariaLabel={`billing period for ${catalog.name}`}
              disabled={toggleDisabled}
              sx={{ mb: 0, gap: 0 }}
            />
          </span>
        </Tooltip>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {priceLabel}
        </Typography>
        {isExpired && onReactivate && (
          <Button
            size="small"
            variant="contained"
            onClick={() => onReactivate(catalog.catalogAliId)}
            data-testid={`catalog-reactivate-${catalog.catalogAliId}`}
          >
            Reactivate
          </Button>
        )}
        {!catalog.isPrimary && onRemove && (
          <Button size="small" color="error" onClick={() => onRemove(catalog.catalogAliId)}>
            Remove
          </Button>
        )}
      </Box>
    </Box>
  );
};
